import { FC, useEffect, useRef, useState } from 'react';
import './App.css';
import { BrowserRouter } from 'react-router-dom';
import { NavigateList } from 'routes/routesList';
import { useIdleTimer } from 'react-idle-timer';
import { useDispatch, useSelector } from 'react-redux';
import { getAuth, getIsAuth } from 'redux/reducers/auth/selectors';
import { getOrganization } from 'redux/reducers/user/selectors';
import { Screensaver } from 'components/Screesnaver';
import {
	refreshTokenByTimeOutSuccess,
	saveUrl,
	setLocalStorageMessage,
} from 'redux/reducers/auth/reducer';
import { PATHS } from 'routes/constants';
import { endpoint } from 'services/endpoint';
import { http } from 'services/http';
import { getAllCountriesRequest } from 'redux/reducers/settings/reducer';

export const App: FC = () => {
	const dispatch = useDispatch();
	const [stateIdle, setStateIdle] = useState<string>('Active');
	const zendeskLoginRequestedRef = useRef(false);

	const loggedIn = useSelector(getIsAuth);
	const auth = useSelector(getAuth);
	const organization = useSelector(getOrganization);

	const onIdle = () => {
		setStateIdle('Idle');
	};

	useIdleTimer({
		onIdle,
		timeout: 900_000,
		throttle: 100_000,
		disabled: !loggedIn || !organization,
	});

	useEffect(() => {
		let cancelled = false;

		if (auth.xMerchantId) {
			if (!loggedIn) {
				zendeskLoginRequestedRef.current = false;
			} else if (!zendeskLoginRequestedRef.current) {
				zendeskLoginRequestedRef.current = true;

				const retryMs = 1000;

				const tryLoginZendesk = () => {
					if (cancelled) return;

					const { zE } = window;
					if (!zE) {
						window.setTimeout(tryLoginZendesk, retryMs);
						return;
					}

					const requestHeaders: Record<string, string> = {
						Accept: 'application/json',
					};
					if (auth.accessToken) requestHeaders.Authorization = `Bearer ${auth.accessToken}`;
					if (auth.xMerchantId) requestHeaders['X-Merchant-Id'] = auth.xMerchantId;

					const fetchJwt = async (): Promise<string> => {
						const response = await http.get(endpoint.zendesk.ZENDESK_USER_TOKEN, {
							headers: requestHeaders,
						});
						return response.data.jwt;
					};

					zE(
						'messenger',
						'loginUser',
						(callback: (token: string) => void) => {
							fetchJwt()
								.then((token) => {
									callback(token);
								})
								.catch((error: unknown) => {
									console.error('Zendesk loginUser: failed to fetch JWT', error);
								});
						},
						(error: unknown) => {
							if (error) {
								console.error('Zendesk loginUser failed', error);
							}
						},
					);
				};

				tryLoginZendesk();
			}
		}

		return () => {
			cancelled = true;
		};
	}, [loggedIn, auth.accessToken, auth.xMerchantId]);

	window.addEventListener('unload', () => {
		const url =
			window.location.pathname === PATHS.SIGN_IN_PAGE
				? PATHS.DASHBOARD_PAGE
				: window.location.pathname + window.location.search;
		dispatch(refreshTokenByTimeOutSuccess());
		dispatch(saveUrl(url));
		dispatch(setLocalStorageMessage(`unload ${new Date().toISOString()}`));
	});

	useEffect(() => {
		if (loggedIn && organization && auth.xMerchantId) {
			dispatch(getAllCountriesRequest());
		}
	}, [dispatch, loggedIn, organization, auth.xMerchantId]);

	return (
		<BrowserRouter future={{ v7_startTransition: true }}>
			<Screensaver
				open={stateIdle === 'Idle'}
				handleClose={() => {
					setStateIdle('Active');
				}}
			/>
			<NavigateList />
		</BrowserRouter>
	);
};
